import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import StarekContext from "../context/StarekContext";

function CardSpinner() {
    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "inherit",
            }}
        >
            <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: "2rem", color: "#6c757d" }}
            />
        </div>
    );
}

// How many rows the card lists. Elapsed windows are historical, so a longer
// (scrollable) list is fine once they're switched on.
const MAX_LIVE_ITEMS = 7;
const MAX_ITEMS_WITH_ELAPSED = 25;

function UpcomingMaintenance({ maintenanceSchedules, loading }) {
    const [collapsed, setCollapsed] = useState(false);
    // Elapsed = the whole window is behind us. Hidden by default so the card
    // answers "what's coming" instead of filling with months-old windows.
    const [showElapsed, setShowElapsed] = useState(false);
    const { types } = useContext(StarekContext);
    const TYPE_ADHOC = types?.maintenance_schedules?.schedule_types?.ADHOC ?? 1;

    const { live, elapsed } = useMemo(() => {
        if (!maintenanceSchedules?.length) return { live: [], elapsed: [] };
        const now = moment();

        const parsed = maintenanceSchedules
            .map((ms) => {
                let attrs = ms.schedule_attributes;
                if (typeof attrs === "string") {
                    try {
                        attrs = JSON.parse(attrs);
                    } catch {
                        attrs = {};
                    }
                }
                const fromDate = attrs?.from ? moment(attrs.from) : null;
                const toDate = attrs?.to ? moment(attrs.to) : fromDate;
                // An ADHOC record only exists while the machine is actually
                // under maintenance (the Devices page deletes it on clear), so
                // it's open-ended - never elapsed, however old its start is.
                const isAdhoc = ms.schedule_type === TYPE_ADHOC;
                const hasEnded =
                    !isAdhoc &&
                    !!toDate &&
                    now.isAfter(moment(toDate).endOf("day"));
                const hasStarted = !!fromDate && now.isSameOrAfter(fromDate);
                return {
                    ...ms,
                    fromDate,
                    toDate,
                    isAdhoc,
                    state: hasEnded
                        ? "elapsed"
                        : hasStarted
                            ? "in-progress"
                            : "upcoming",
                };
            })
            .filter((ms) => ms.fromDate);

        return {
            // Soonest first - in-progress work sits at the top of what's live.
            live: parsed
                .filter((ms) => ms.state !== "elapsed")
                .sort((a, b) => a.fromDate - b.fromDate),
            // Most recently finished first - the useful end of the history.
            elapsed: parsed
                .filter((ms) => ms.state === "elapsed")
                .sort((a, b) => b.toDate - a.toDate),
        };
    }, [maintenanceSchedules, TYPE_ADHOC]);

    const items = useMemo(
        () =>
            showElapsed
                ? [...live, ...elapsed].slice(0, MAX_ITEMS_WITH_ELAPSED)
                : live.slice(0, MAX_LIVE_ITEMS),
        [live, elapsed, showElapsed],
    );

    const daysUntil = (date) => {
        const d = moment(date)
            .startOf("day")
            .diff(moment().startOf("day"), "days");
        return d;
    };

    const badgeFor = (ms) => {
        if (ms.state === "elapsed") {
            const daysSince = -daysUntil(ms.toDate);
            return { color: "#6c757d", label: `${daysSince}d ago` };
        }
        if (ms.state === "in-progress") {
            return { color: "#fd7e14", label: ms.isAdhoc ? "Under maint." : "In progress" };
        }
        const days = daysUntil(ms.fromDate);
        if (days === 0) return { color: "#dc3545", label: "Today" };
        if (days <= 3) return { color: "#fd7e14", label: `${days}d` };
        if (days <= 7) return { color: "#ffc107", label: `${days}d` };
        return { color: "#28a745", label: `${days}d` };
    };

    return (
        <div
            className={`card card-outline card-warning dashboard-monitor-card ${collapsed ? "collapsed-card" : ""}`}
            style={{ position: "relative" }}
        >
            {loading && <CardSpinner />}
            <div className="card-header py-2">
                <h3 className="card-title">
                    <i className="fa fa-cogs mr-2" />
                    Upcoming Maintenance
                </h3>
                <div className="card-tools d-flex align-items-center">
                    <div className="custom-control custom-checkbox mr-2">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id="upcoming_maintenance_show_elapsed"
                            checked={showElapsed}
                            onChange={(e) => setShowElapsed(e.target.checked)}
                        />
                        <label
                            className="custom-control-label text-muted"
                            htmlFor="upcoming_maintenance_show_elapsed"
                            style={{ fontSize: "0.75rem", fontWeight: 400, cursor: "pointer" }}
                            title="Also list maintenance windows that have already passed"
                        >
                            Show Elapsed
                            {elapsed.length > 0 && ` (${elapsed.length})`}
                        </label>
                    </div>
                    <Link
                        to="/maintenance"
                        className="btn btn-tool btn-sm text-muted"
                    >
                        <i className="fa fa-external-link" />
                    </Link>
                    <button
                        type="button"
                        className="btn btn-tool"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <i
                            className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`}
                        />
                    </button>
                </div>
            </div>
            <div
                className={`card-body p-0 ${collapsed ? "d-none" : ""}`}
                style={{ overflowY: "auto" }}
            >
                {items.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                            style={{
                                width: 50,
                                height: 50,
                                background: "rgba(255,193,7,0.12)",
                            }}
                        >
                            <i className="fa fa-cogs fa-lg text-warning" />
                        </div>
                        <p className="text-muted small mb-0">
                            {!showElapsed && elapsed.length > 0
                                ? `Nothing upcoming. ${elapsed.length} elapsed window${elapsed.length === 1 ? "" : "s"} — tick "Show Elapsed" to list them.`
                                : "No scheduled maintenance on record."}
                        </p>
                    </div>
                ) : (
                    <div
                        className="d-flex flex-column"
                        style={{ gap: 8, padding: "10px 12px" }}
                    >
                        {items.map((ms) => {
                            const badge = badgeFor(ms);
                            const isElapsed = ms.state === "elapsed";
                            return (
                                <div
                                    key={ms.id}
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        borderLeft: `3px solid ${badge.color}`,
                                        borderRadius: 6,
                                        background: "#fafafa",
                                        border: "1px solid #e9ecef",
                                        borderLeftWidth: 3,
                                        borderLeftColor: badge.color,
                                        padding: "8px 12px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                        opacity: isElapsed ? 0.65 : 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            overflow: "hidden",
                                            minWidth: 0,
                                        }}
                                    >
                                        <div
                                            className="font-weight-bold text-truncate"
                                            style={{ fontSize: "0.82rem" }}
                                            title={ms.name}
                                        >
                                            {ms.state === "in-progress" && (
                                                <i className="fa fa-wrench text-warning mr-1" />
                                            )}
                                            {ms.name}
                                        </div>
                                        <div
                                            className="text-muted"
                                            style={{ fontSize: "0.72rem" }}
                                        >
                                            {ms.fromDate.format("MMM D")}
                                            {ms.toDate &&
                                                !ms.fromDate.isSame(
                                                    ms.toDate,
                                                    "day",
                                                ) &&
                                                ` – ${ms.toDate.format("MMM D")}`}
                                        </div>
                                    </div>
                                    <span
                                        className="badge ml-2 flex-shrink-0"
                                        style={{
                                            background: badge.color,
                                            color: "#fff",
                                            fontSize: "0.72rem",
                                            minWidth: 40,
                                            textAlign: "center",
                                            borderRadius: 4,
                                        }}
                                    >
                                        {badge.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className={`card-footer py-1 ${collapsed ? "d-none" : ""}`}>
                <div className="starek-card-footer">
                    <Link to="/maintenance" className="starek-card-footer-link">
                        View all maintenance schedules{" "}
                        <i className="fa fa-arrow-right" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default UpcomingMaintenance;
